/*
 Title: timeCore.ts
 Purpose: Strict RFC3339 instant parsing and timezone/day-key helpers
 Last-Modified: 2026-02-21
 Notes: Keep parsing deterministic; reject ambiguous timestamps.
*/

const RFC3339_INSTANT_PATTERN =
  /^(?<year>\d{4})-(?<month>\d{2})-(?<day>\d{2})T(?<hour>\d{2}):(?<minute>\d{2}):(?<second>\d{2})(?<fraction>\.\d{1,9})?(?<zone>Z|[+-]\d{2}:\d{2})$/;

const UTC_TIME_ZONE = "UTC";
const DAY_KEY_FORMATTER_CACHE = new Map<string, Intl.DateTimeFormat>();

type ParsedParts = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
  zone: string;
};

function parseNumericParts(value: string): ParsedParts | null {
  const match = RFC3339_INSTANT_PATTERN.exec(value);
  if (!match?.groups) return null;
  const year = Number(match.groups.year);
  const month = Number(match.groups.month);
  const day = Number(match.groups.day);
  const hour = Number(match.groups.hour);
  const minute = Number(match.groups.minute);
  const second = Number(match.groups.second);
  const zone = match.groups.zone;
  if (typeof zone !== "string") return null;
  if (![year, month, day, hour, minute, second].every(Number.isFinite)) return null;
  return { year, month, day, hour, minute, second, zone };
}

function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

function isValidRange(parts: ParsedParts): boolean {
  if (parts.month < 1 || parts.month > 12) return false;
  if (parts.day < 1 || parts.day > daysInMonth(parts.year, parts.month)) return false;
  if (parts.hour < 0 || parts.hour > 23) return false;
  if (parts.minute < 0 || parts.minute > 59) return false;
  if (parts.second < 0 || parts.second > 59) return false;
  if (parts.zone === "Z") return true;
  const sign = parts.zone.charAt(0);
  const [offsetHoursRaw, offsetMinutesRaw] = parts.zone.slice(1).split(":");
  if (offsetHoursRaw === undefined || offsetMinutesRaw === undefined) return false;
  const offsetHours = Number(offsetHoursRaw);
  const offsetMinutes = Number(offsetMinutesRaw);
  if (!["+", "-"].includes(sign)) return false;
  if (!Number.isFinite(offsetHours) || !Number.isFinite(offsetMinutes)) return false;
  if (offsetHours < 0 || offsetHours > 23) return false;
  if (offsetMinutes < 0 || offsetMinutes > 59) return false;
  return true;
}

function formatterForTimeZone(timeZone: string): Intl.DateTimeFormat {
  const cached = DAY_KEY_FORMATTER_CACHE.get(timeZone);
  if (cached) return cached;
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  DAY_KEY_FORMATTER_CACHE.set(timeZone, formatter);
  return formatter;
}

export function parseRfc3339Instant(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parts = parseNumericParts(trimmed);
  if (!parts || !isValidRange(parts)) return null;
  const parsed = Date.parse(trimmed);
  if (Number.isNaN(parsed)) return null;
  return parsed;
}

export function isRfc3339Instant(value: string): boolean {
  return parseRfc3339Instant(value) !== null;
}

export function toCanonicalUtcIso(value?: string | Date): string | undefined {
  if (!value) return undefined;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? undefined : value.toISOString();
  }
  const parsed = parseRfc3339Instant(value);
  if (parsed === null) return undefined;
  return new Date(parsed).toISOString();
}

export function isValidIanaTimeZone(value?: string | null): boolean {
  if (!value) return false;
  const trimmed = value.trim();
  if (!trimmed) return false;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: trimmed });
    return true;
  } catch {
    return false;
  }
}

export function resolveCanonicalTimeZone(preferred?: string | null): string {
  if (isValidIanaTimeZone(preferred)) {
    return String(preferred).trim();
  }
  const runtimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  if (isValidIanaTimeZone(runtimeZone)) {
    return runtimeZone;
  }
  return UTC_TIME_ZONE;
}

export function toDayKeyForEpochMs(epochMs: number, preferredTimeZone?: string | null): string {
  const zone = resolveCanonicalTimeZone(preferredTimeZone);
  const formatter = formatterForTimeZone(zone);
  const parts = formatter.formatToParts(new Date(epochMs));
  const year = parts.find((part) => part.type === "year")?.value;
  const month = parts.find((part) => part.type === "month")?.value;
  const day = parts.find((part) => part.type === "day")?.value;
  if (!year || !month || !day) {
    return new Date(epochMs).toISOString().slice(0, 10);
  }
  return `${year}-${month}-${day}`;
}

export function toDayKeyForTimestamp(value: string, preferredTimeZone?: string | null): string | null {
  const epochMs = parseRfc3339Instant(value);
  if (epochMs === null) return null;
  return toDayKeyForEpochMs(epochMs, preferredTimeZone);
}
